"use strict";
/**
 * Due to how frequently it is used it is imperative the logic fails gracefully instead of erroring
 *
 * Description logic overview:
 * 1. If the schedule needs to be converted to "local" time, do so
 * 2. If the schedule is HOURLY or DAILY:
 *      If it is not a 24h recurrence it will take the form of [days of week][recurrence interval][starting from][ending at]
 *        a. If it is every day of the week [days of week] will be with "All days of the week,"
 *           Else [days of week] will be the shortened version days of the week ("Every Mon, Wed, Fri,")
 *        b. If the recurrence is an integer number of hours then [recurrence interval] will be "every X hours"
 *           Else [recurrence interval] will be "every X minutes" (even if X is a non integer amount)
 *        c. If there is no end time,, or it is the same as the start time, [starting from] will be "at X"
 *           Else [starting from] will be "from X"
 *        d. [ending at] will be "to X"
 *      Else it will be in the form of [days of the week][at
 *        a. where [at] will be "at X"
 *    If the schedule is WEEKLY:
 *      It will take the form of [days of the week][at]
 *      a. Same as HOURLY/DAILY [days of the week]
 *      b. [at] will be "at X"
 *    If the schedule is MONTHLY:
 *      It will take the form of [days of month][at]
 *      a. If the daysOfWeek field is set then [days of month] will take the form of [nth][weekday]
 *         i. [nth] will be "First"/"Second"/...
 *         ii.[weekday] will be the long form of the day of the week ("Monday", "Friday")
 *         Else
 *            If every day of the month is selected [days of month] will be "Every day of the month"
 *            Else [days of month] will be a comma seperated list of the days ("1st, 2nd, 3rd")
 *      b. [at] will be "at X"
 */
Object.defineProperty(exports, "__esModule", { value: true });
var Recurrence_1 = require("./components/Recurrence");
var Localize_1 = require("../gen-src/Localize");
var SelfServiceSchedules_1 = require("./SelfServiceSchedules");
var ScheduleDescriptions;
(function (ScheduleDescriptions) {
    function localizeMediumLegnthDayOfWeek(dayOfWeek) {
        switch (dayOfWeek) {
            case Recurrence_1.RecurrenceDaysOfTheWeek.Sat:
                return Localize_1.Messages.RecurrenceDayOfWeekSaturdayMedium();
            case Recurrence_1.RecurrenceDaysOfTheWeek.Sun:
                return Localize_1.Messages.RecurrenceDayOfWeekSundayMedium();
            case Recurrence_1.RecurrenceDaysOfTheWeek.Mon:
                return Localize_1.Messages.RecurrenceDayOfWeekMondayMedium();
            case Recurrence_1.RecurrenceDaysOfTheWeek.Tue:
                return Localize_1.Messages.RecurrenceDayOfWeekTuesdayMedium();
            case Recurrence_1.RecurrenceDaysOfTheWeek.Wed:
                return Localize_1.Messages.RecurrenceDayOfWeekWednesdayMedium();
            case Recurrence_1.RecurrenceDaysOfTheWeek.Thu:
                return Localize_1.Messages.RecurrenceDayOfWeekThursdayMedium();
            case Recurrence_1.RecurrenceDaysOfTheWeek.Fri:
                return Localize_1.Messages.RecurrenceDayOfWeekFridayMedium();
            default:
                throw "Attempted to localize (medium) an unknown day of the week " + JSON.stringify(dayOfWeek);
        }
    }
    ScheduleDescriptions.localizeMediumLegnthDayOfWeek = localizeMediumLegnthDayOfWeek;
    function localizeDaysOfWeek(daysOfWeek) {
        // Preconditions
        if (!daysOfWeek) {
            throw "Attempted to describe null/undefined days of the week";
        }
        if (daysOfWeek.length === 0) {
            throw "Attempted to describe 0 days of the week: " + JSON.stringify(daysOfWeek);
        }
        if (daysOfWeek.length > 7) {
            throw "Attempted to describe more than 7 days of the week: " + JSON.stringify(daysOfWeek);
        }
        // If it is every day of the week
        if (daysOfWeek.length === 7) {
            // [days of week] will be with "All days of the week"
            return Localize_1.Messages.RecurrenceCaptionEveryDayOfWeek();
        }
        var expectedOrdering = SelfServiceSchedules_1.StaticInitializers.DEFAULT_ALL_DAYS_A_WEEK();
        var toNum = function (day) { return expectedOrdering.indexOf(day); };
        // Else [days of week] will be the shortened version days of the week ("Every Mon, Wed, Fri,")
        // (Sorted such that monday comes first)
        var localizedDaysOfWeek = daysOfWeek
            .sort(function (day1, day2) { return toNum(day1) - toNum(day2); })
            .map(function (day) { return localizeMediumLegnthDayOfWeek(day); }).join(', ');
        return Localize_1.Messages.RecurrenceEveryDaysOfWeek({ DAYS_OF_WEEK_LOC: localizedDaysOfWeek });
    }
    ScheduleDescriptions.localizeDaysOfWeek = localizeDaysOfWeek;
    // If the recurrence is an integer number of hours then [recurrence interval] will be "every X hours"
    // Else [recurrence interval] will be "every X minutes" (even if X is a non integer amount)
    function localizeRecurrenceMinutes(numMinutes) {
        // Preconditions
        numMinutes = Math.round(numMinutes); // Every 3.1415 minutes doesn't quite have the right ring to it
        if (numMinutes < 0 || numMinutes > SelfServiceSchedules_1.StaticInitializers.MINUTES_IN_DAY) {
            throw "Attempted to describe recurrence with invalid minutes: " + numMinutes;
        }
        // If the recurrence is an integer number of hours then [recurrence interval] will be "every X hours"
        if (numMinutes % 60 === 0) {
            var numHours = numMinutes / 60;
            // Special case for "every hour" vs "every 2 hours"
            return numHours === 1
                ? Localize_1.Messages.RecurrenceEveryHour()
                : Localize_1.Messages.RecurrenceEveryHours({ NUM_HOURS: String(numHours) });
        }
        // Else [recurrence interval] will be [every X minutes] (even if X is a non integer amount)
        //   Note: we don't have a special message for the 1 minute case because its not officially supported
        return Localize_1.Messages.RecurrenceEveryMinutes({ NUM_MINUTES: String(numMinutes) });
    }
    ScheduleDescriptions.localizeRecurrenceMinutes = localizeRecurrenceMinutes;
    // Turns a time in minutes after midnight to a human readable string
    function formatTime(minutesAfterMidnight) {
        // Preconditions
        var isDefined = minutesAfterMidnight !== undefined && !isNaN(minutesAfterMidnight);
        var isInValidRange = minutesAfterMidnight >= 0 && minutesAfterMidnight <= SelfServiceSchedules_1.StaticInitializers.MINUTES_IN_DAY;
        if (!isDefined || !isInValidRange) {
            throw "Failed to format time due to invalid minutes after midnight: " + minutesAfterMidnight;
        }
        var date = new Date();
        date.setHours(minutesAfterMidnight / 60);
        date.setMinutes(minutesAfterMidnight % 60);
        return Localize_1.Formatters.formatShortTime(date);
    }
    ScheduleDescriptions.formatTime = formatTime;
    function localizeStartAndEndTime(startTime, endTime) {
        // Preconditions
        if (startTime === undefined || startTime === null) {
            throw "Failed to get localized start/end time due to invalid start time: " + startTime;
        }
        var startTimeLocalized = formatTime(startTime);
        // If there is no end time, or it is the same as the start time, [starting from] will be "at X"
        if (endTime === undefined || endTime === null || endTime === startTime) {
            return Localize_1.Messages.RecurrenceStartingAt({ START_AT: startTimeLocalized });
        }
        var endTimeLocalized = formatTime(endTime);
        // Else [starting from] will be "from X"
        // [ending at] will be "to X"
        return Localize_1.Messages.RecurrenceStrGeneralFromTo({ START_TIME: startTimeLocalized, END_TIME: endTimeLocalized });
    }
    ScheduleDescriptions.localizeStartAndEndTime = localizeStartAndEndTime;
    function localizeAtTime(atTime) {
        var atTimeLocalized = formatTime(atTime);
        return Localize_1.Messages.AtTime({ TIME: atTimeLocalized });
    }
    ScheduleDescriptions.localizeAtTime = localizeAtTime;
    // ii.[weekday] will be the long form of the day of the week ("Monday", "Friday")
    // Else
    // If every day of the month is selected [days of month] will be "Every day of the month"
    // Else [days of month] will be a comma seperated list of the days ("1st, 2nd, 3rd")
    // [nth] will be "First"/"Second"/... (ordinal)
    function localizeOrdinal(ordinal) {
        switch (ordinal) {
            case 0:
                // Special case signifying last
                return Localize_1.Messages.Last();
            case 1:
                return Localize_1.Messages.First();
            case 2:
                return Localize_1.Messages.Second();
            case 3:
                return Localize_1.Messages.Third();
            case 4:
                return Localize_1.Messages.Fourth();
            case 5:
                return Localize_1.Messages.Fifth();
            default:
                throw "Attempted to localize unsupported ordinal value: " + ordinal;
        }
    }
    ScheduleDescriptions.localizeOrdinal = localizeOrdinal;
    function localizeFullDayOfWeek(dayOfWeek) {
        switch (dayOfWeek) {
            case Recurrence_1.RecurrenceDaysOfTheWeek.Mon:
                return Localize_1.Messages.Monday();
            case Recurrence_1.RecurrenceDaysOfTheWeek.Tue:
                return Localize_1.Messages.Tuesday();
            case Recurrence_1.RecurrenceDaysOfTheWeek.Wed:
                return Localize_1.Messages.Wednesday();
            case Recurrence_1.RecurrenceDaysOfTheWeek.Thu:
                return Localize_1.Messages.Thursday();
            case Recurrence_1.RecurrenceDaysOfTheWeek.Fri:
                return Localize_1.Messages.Friday();
            case Recurrence_1.RecurrenceDaysOfTheWeek.Sat:
                return Localize_1.Messages.Saturday();
            case Recurrence_1.RecurrenceDaysOfTheWeek.Sun:
                return Localize_1.Messages.Sunday();
            default:
                throw "Attempted to localize full day of week for invalid day of week: " + dayOfWeek;
        }
    }
    ScheduleDescriptions.localizeFullDayOfWeek = localizeFullDayOfWeek;
    // Needed to turn 1 into 1ˢᵗ, 2 into 2ⁿᵈ, etc...
    function localizeToSuperscriptedNumber(dayOfMonth) {
        switch (dayOfMonth) {
            case 1:
                return Localize_1.Messages.Superscript1St();
            case 2:
                return Localize_1.Messages.Superscript2Nd();
            case 3:
                return Localize_1.Messages.Superscript3Rd();
            default:
                return Localize_1.Messages.SuperscriptNTh({ N: String(dayOfMonth) });
        }
    }
    ScheduleDescriptions.localizeToSuperscriptedNumber = localizeToSuperscriptedNumber;
    function localizeDaysOfMonth(daysOfMonth) {
        var anyInvalidDays = daysOfMonth.filter(function (day) { return day < 0 || day > 31 || Math.round(day) !== day; }).length > 0;
        if (anyInvalidDays) {
            throw "Attempted to localize invalid days of the month: " + daysOfMonth;
        }
        if (daysOfMonth.length === 31) {
            return Localize_1.Messages.DescriptionMonthlyAllDays();
        }
        if (daysOfMonth.length === 1 && daysOfMonth[0] === 0) {
            return Localize_1.Messages.DescriptionMonthlyDays({
                DAYS_OF_MONTH: Localize_1.Messages.LastDayOfMonth(),
            });
        }
        var daysOfMonthLocalized = daysOfMonth.sort(function (a, b) { return a - b; }).map(localizeToSuperscriptedNumber).join(', ');
        return Localize_1.Messages.DescriptionMonthlyDays({
            DAYS_OF_MONTH: daysOfMonthLocalized,
        });
    }
    ScheduleDescriptions.localizeDaysOfMonth = localizeDaysOfMonth;
    function localizeOrdinalMonthlyRecurrence(daysOfMonth, daysOfWeek) {
        // Preconditions
        // Note: 0 is a valid day of month in this case
        if (daysOfMonth.length !== 1 || daysOfMonth[0] < 0 || daysOfMonth[0] > 31) {
            throw "Attempted to localize ordinal monthly recurrence with invalid days of month: " + daysOfMonth;
        }
        if (daysOfWeek.length !== 1) {
            throw "Attempted to localize ordinal monthly recurrence with invalid days of week: " + daysOfWeek;
        }
        var ordinal = localizeOrdinal(daysOfMonth[0]); // First/Second/...
        var weekday = localizeFullDayOfWeek(daysOfWeek[0]); // Tuesday/...
        return Localize_1.Messages.DescriptionMonthlyOrdinal({
            ORDINAL: ordinal,
            DAY_OF_WEEK: weekday,
        });
    }
    ScheduleDescriptions.localizeOrdinalMonthlyRecurrence = localizeOrdinalMonthlyRecurrence;
    function mod(val, by) {
        // From https://stackoverflow.com/a/4467559
        return ((val % by) + by) % by;
    }
    ScheduleDescriptions.mod = mod;
    function shiftDaysOfWeek(daysOfWeek, shiftByDays) {
        var allDaysOfWeek = SelfServiceSchedules_1.StaticInitializers.DEFAULT_ALL_DAYS_A_WEEK();
        return daysOfWeek.map(function (day) {
            return allDaysOfWeek[mod(allDaysOfWeek.indexOf(day) + shiftByDays, 7)];
        });
    }
    ScheduleDescriptions.shiftDaysOfWeek = shiftDaysOfWeek;
    function localizeRecurrence(recurrence) {
        try {
            switch (recurrence.recurrenceType) {
                case Recurrence_1.RecurrenceType.Hourly:
                case Recurrence_1.RecurrenceType.Daily: {
                    var daysOfWeek = localizeDaysOfWeek(recurrence.recurrenceDetails.recurrenceDaysOfWeek);
                    // If it is not a 24h recurrence it will take the form of [days of week][recurrence interval][starting from][ending at]
                    if (recurrence.recurrenceDetails.recurrenceMinutes !== SelfServiceSchedules_1.StaticInitializers.MINUTES_IN_DAY) {
                        var recurrenceInterval = localizeRecurrenceMinutes(recurrence.recurrenceDetails.recurrenceMinutes);
                        var startingFromEndingAt = localizeStartAndEndTime(recurrence.recurrenceDetails.startTime, recurrence.recurrenceDetails.endTime);
                        return Localize_1.Messages.DescriptionDailyHourly({
                            EVERY_DAYS_OF_THE_WEEK: daysOfWeek,
                            RECURRING_EVERY: recurrenceInterval,
                            STARTING_FROM_ENDING_AT: startingFromEndingAt,
                        });
                    }
                    // Else it will be in the form of [days of the week][at]
                    var at_1 = localizeAtTime(recurrence.recurrenceDetails.startTime);
                    return Localize_1.Messages.DescriptionWeekly({ EVERY_DAYS_OF_WEEK: daysOfWeek, AT: at_1 });
                }
                case Recurrence_1.RecurrenceType.Weekly: {
                    // It will take the form of [days of the week][at]
                    var daysOfWeek = localizeDaysOfWeek(recurrence.recurrenceDetails.recurrenceDaysOfWeek);
                    var at_2 = localizeAtTime(recurrence.recurrenceDetails.startTime);
                    return Localize_1.Messages.DescriptionWeekly({ EVERY_DAYS_OF_WEEK: daysOfWeek, AT: at_2 });
                }
                case Recurrence_1.RecurrenceType.Monthly:
                    // It will take the form of [days of month][at]
                    // [at] will be "at X"
                    var at = localizeAtTime(recurrence.recurrenceDetails.startTime);
                    var daysOfMonth = Recurrence_1.RecurrenceConverters.toDaysOfMonthArr(recurrence.recurrenceDetails.recurrenceDayOfMonth);
                    // If the daysOfWeek field is set then [days of month] will take the form of [nth][weekday]
                    if (recurrence.recurrenceDetails.recurrenceDaysOfWeek && recurrence.recurrenceDetails.recurrenceDaysOfWeek.length > 0) {
                        var daysOfWeek = recurrence.recurrenceDetails.recurrenceDaysOfWeek;
                        var ordinalMonthly = localizeOrdinalMonthlyRecurrence(daysOfMonth, daysOfWeek);
                        return Localize_1.Messages.DescriptionMonthly({
                            DAYS_OF_MONTH: ordinalMonthly,
                            AT: at,
                        });
                    }
                    var localizedDaysOfMonth = localizeDaysOfMonth(daysOfMonth);
                    return Localize_1.Messages.DescriptionMonthly({
                        DAYS_OF_MONTH: localizedDaysOfMonth,
                        AT: at,
                    });
                default:
                    // Worst case we describe it as empty. This is of course not desired, but is better than crashing the page
                    // We can't throw because that would crash the page
                    console.error("Unexpected recurrence type for:\n " + JSON.stringify(recurrence));
                    return '';
            }
        }
        catch (exception) {
            // Again - worst case we describe it as empty. This is of course not desired, but is better than crashing the page
            // We can't throw because that would crash the page
            console.error("Unexpected error whilst localizing recurrence:\n " + JSON.stringify(recurrence) + "\n\nError:" + exception);
            return '';
        }
    }
    ScheduleDescriptions.localizeRecurrence = localizeRecurrence;
    function shiftRecurrence(recurrence, shiftByMinutes) {
        // We will be modifying the object so we need our own copy
        var result = JSON.parse(JSON.stringify(recurrence));
        // Determine what the new start time will be, and if it ends up being on a different day
        var newStartTime = result.recurrenceDetails.startTime + shiftByMinutes;
        // Day offset is -1 if start time goes negative, +1 if start time goes into next day
        var dayOffset = Math.floor(newStartTime / SelfServiceSchedules_1.StaticInitializers.MINUTES_IN_DAY);
        // Once we know what the num days offset is we can get the time for the actual day it runs on
        newStartTime = mod(newStartTime, SelfServiceSchedules_1.StaticInitializers.MINUTES_IN_DAY);
        result.recurrenceDetails.startTime = newStartTime;
        // Determine what the new end time will be, if an end time is defined
        if (result.recurrenceType === Recurrence_1.RecurrenceType.Hourly || result.recurrenceType === Recurrence_1.RecurrenceType.Daily) {
            var newEndTime = result.recurrenceDetails.endTime + shiftByMinutes;
            // We already know what day it will run on thanks to start time, so no need to compute day offset
            // If the end is on a different day that will not matter for the description
            newEndTime = mod(newEndTime, SelfServiceSchedules_1.StaticInitializers.MINUTES_IN_DAY);
            result.recurrenceDetails.endTime = newEndTime;
        }
        // We do month-day shifting before week-day shifting because the former depends on the latter
        if (result.recurrenceType === Recurrence_1.RecurrenceType.Monthly) {
            // Technically only schedules with exactly 1 day are valid ordinal ones, but really any non-zero days of week in a monthly
            // schedule mean ordinal
            var isOrdinal = result.recurrenceDetails.recurrenceDaysOfWeek && result.recurrenceDetails.recurrenceDaysOfWeek.length > 0;
            var daysOfMonth = Recurrence_1.RecurrenceConverters.toDaysOfMonthArr(result.recurrenceDetails.recurrenceDayOfMonth);
            if (isOrdinal) {
                // If it's an ordinal monthly schedule we need to shifter the week number
                if (daysOfMonth.length !== 1) {
                    throw "Attempted to shift ordinal monthly schedule with multiple days of month";
                }
                var ordinalOfMonth = Recurrence_1.RecurrenceConverters.toDaysOfMonthArr(result.recurrenceDetails.recurrenceDayOfMonth)[0];
                var daysOfWeek = result.recurrenceDetails.recurrenceDaysOfWeek;
                if (daysOfWeek.length !== 1) {
                    throw "Attempted to shift ordinal monthly schedule multiple days of week";
                }
                var allDaysOfWeek = SelfServiceSchedules_1.StaticInitializers.DEFAULT_ALL_DAYS_A_WEEK();
                var currentDayOfWeekIndex = allDaysOfWeek.indexOf(daysOfWeek[0]);
                var weekOffset = Math.floor((currentDayOfWeekIndex + dayOffset) / 7);
                // Shift by the day offset
                ordinalOfMonth = mod(ordinalOfMonth + weekOffset, 6);
                // If the value is 0 that would mean "Last" but actually it should be the first week
                // (as the week "after" the 5th one isn't that last one, due to a month having a max of 5 weeks)
                // but if we're going backwards, the previous week to the first of the month _is_ the last week
                // of the previous month
                if (ordinalOfMonth === 0 && weekOffset > 0) { // TODO: Condition never happens with weekoffset > 0
                    ordinalOfMonth = 1;
                }
                result.recurrenceDetails.recurrenceDayOfMonth = String(Recurrence_1.RecurrenceConverters.toDaysOfMonthMask([ordinalOfMonth]));
            }
            else {
                if (!daysOfMonth) {
                    throw "undefined days of month for monthly \n" + JSON.stringify(recurrence);
                }
                // Just shift the days of the month
                daysOfMonth = daysOfMonth.map(function (day) {
                    day = mod(day + dayOffset, 32);
                    // If the value is 0 that would mean "Last" but actually it should be the first day
                    // (as the day "after" the 31st one isn't that last one, due to a month having a max of 31 days)
                    // but backwards, the day before the 1st of the month is the last of the previous month
                    if (day === 0 && dayOffset > 0) {
                        day = 1;
                    }
                    // Similarly, if we take look at the day before the last day of the month, it will NOT be the 31st
                    // as there are no 32 month days
                    if (day === 31 && dayOffset < 0) {
                        day = 30;
                    }
                    return day;
                });
                result.recurrenceDetails.recurrenceDayOfMonth = String(Recurrence_1.RecurrenceConverters.toDaysOfMonthMask(daysOfMonth));
            }
        }
        // Determine what the days of week are
        result.recurrenceDetails.recurrenceDaysOfWeek = shiftDaysOfWeek(result.recurrenceDetails.recurrenceDaysOfWeek, dayOffset);
        return result;
    }
    ScheduleDescriptions.shiftRecurrence = shiftRecurrence;
    function fixAllDayStartTime(recurrence) {
        // We will be modifying the object so we need our own copy
        var result = JSON.parse(JSON.stringify(recurrence));
        // No-op if not an hourly or daily schedule
        if (recurrence.recurrenceType !== Recurrence_1.RecurrenceType.Hourly && recurrence.recurrenceType !== Recurrence_1.RecurrenceType.Daily) {
            return result;
        }
        var details = result.recurrenceDetails;
        // No-op if not an all day schedule
        if (details.startTime !== details.endTime) {
            return result;
        }
        var newStartTime = mod(details.startTime, details.recurrenceMinutes);
        details.startTime = newStartTime;
        details.endTime = newStartTime;
        return result;
    }
    ScheduleDescriptions.fixAllDayStartTime = fixAllDayStartTime;
    function localizeRecurrenceWithOffset(recurrence, conversionParams) {
        try {
            if (conversionParams) {
                // If it's +2UTC here, and +4UTC server time
                // then we want to move the schedule description down 2 hours
                var offset = conversionParams.localTimeUtcOffsetMinutes - conversionParams.serverTimeUtcOffsetMinutes;
                var shiftedRecurrence = shiftRecurrence(recurrence, offset);
                var allDayCorrectedRecurrence_1 = fixAllDayStartTime(shiftedRecurrence);
                return localizeRecurrence(allDayCorrectedRecurrence_1);
            }
            var allDayCorrectedRecurrence = fixAllDayStartTime(recurrence);
            return localizeRecurrence(allDayCorrectedRecurrence);
        }
        catch (exception) {
            // Worst case we return an empty string to avoid crashing the page
            // tslint:disable-next-line: no-console
            console.error("Unexpected error whilst localizing offset recurrence:\n " + JSON.stringify(recurrence) + "\n\nError:" + exception);
            return '';
        }
    }
    ScheduleDescriptions.localizeRecurrenceWithOffset = localizeRecurrenceWithOffset;
})(ScheduleDescriptions = exports.ScheduleDescriptions || (exports.ScheduleDescriptions = {}));
//# sourceMappingURL=ScheduleDescriptions.js.map